import React, { useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
interface ToolTipProps {
  content: string;
  children: React.ReactNode;
  position?: "top" | "right" | "bottom" | "left";
  delay?: 100;
  stickyClick?: false;
  iconPositionCenter?: boolean;
}

interface TooltipIconProps {
  icon: IconDefinition;
  message: string;
  position?: "top" | "right" | "bottom" | "left";
  stickyClick?: false;
  delay?: 100;
  direction?: string;
  iconPositionCenter?: boolean;
}

const ToolTip: React.FC<ToolTipProps> = ({
  content,
  delay,
  children,
  position = "top",
  stickyClick,
  iconPositionCenter = false,
}) => {
  const [isActive, setActive] = useState(false);
  const [clickedin, setClickedin] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const timeout = useRef<number | undefined>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const ARROW_SIZE = 6;
  const TOOLTIP_MARGIN = 9;

  const calcStyle = useCallback((): React.CSSProperties => {
    if (!wrapperRef.current) return {};
    const rect = wrapperRef.current.getBoundingClientRect();
    const base: React.CSSProperties = { position: "fixed", zIndex: 9999 };
    switch (position) {
      case "top":
        return {
          ...base,
          left: rect.left + rect.width / 2,
          top: rect.top - ARROW_SIZE,
          transform: "translate(-50%, -100%)",
        };
      case "bottom":
        return {
          ...base,
          left: rect.left + rect.width / 2,
          top: rect.bottom + ARROW_SIZE,
          transform: "translateX(-50%)",
        };
      case "right":
        return {
          ...base,
          left: rect.right + TOOLTIP_MARGIN,
          top: rect.top + rect.height / 2,
          transform: "translateY(-50%)",
        };
      case "left":
        return {
          ...base,
          left: rect.left - TOOLTIP_MARGIN,
          top: rect.top + rect.height / 2,
          transform: "translate(-100%, -50%)",
        };
      default:
        return base;
    }
  }, [position]);

  function showTip() {
    timeout.current = setTimeout(() => {
      setTooltipStyle(calcStyle());
      setActive(true);
    }, delay);
  }

  function hideTip() {
    if (!clickedin) {
      clearInterval(timeout.current);
      setActive(false);
    }
  }

  function toggle() {
    setActive(!clickedin);
    setClickedin(!clickedin);
  }

  return (
    <div
      ref={wrapperRef}
      className={`tooltip-wrapper${isActive ? " tooltip-open" : ""} ${iconPositionCenter ? "tooltip-content-center" : ""}`}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onClick={stickyClick ? toggle : () => {}}
    >
      {children}
      {isActive &&
        content.length > 0 &&
        ReactDOM.createPortal(
          <div
            className={`tooltip-tip tooltip-tip--portal ${position}`}
            style={tooltipStyle}
          >
            {content}
          </div>,
          document.body,
        )}
    </div>
  );
};

const TooltipIcon: React.FC<TooltipIconProps> = ({
  icon,
  message,
  position = "top",
  stickyClick,
  delay,
  iconPositionCenter = false,
}) => {
  return (
    <ToolTip
      content={message}
      position={position}
      stickyClick={stickyClick}
      iconPositionCenter={iconPositionCenter}
      delay={delay}
    >
      <FontAwesomeIcon icon={icon} />
    </ToolTip>
  );
};

export default TooltipIcon;
